import { Component, OnDestroy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Subscription } from 'rxjs'

import { AppInjector } from './app-injector'
import { Message } from './message/models/message.model'
import { AppError } from './message/models/error.model'
import { ErrorDialogComponent } from '@app/shared/dialog/components/error-dialog.component'
import { MessageDialogComponent } from '@app/shared/dialog/components/message-dialog.component'
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog'

/**
 * Defines all available display types.
 */
export enum DisplayType {
  /**
   * Displays a popup
   */
  popup = 0,

  /**
   * Displays a toast
   */
  toast = 1
}

/**
 * Base component extended by other components of the app.
 * Provides methods to interact and handle common needs of an app's component.
 */
@Component({
    template: '',
    standalone: false
})
export class BaseComponent implements OnDestroy {
  /**
   * Defines toast options.
   * duration : Max time of display for a toast (in ms)
   */
  private readonly TOAST_CONFIG: MatSnackBarConfig = {
    duration: 5000
  }

  /**
   * Translate service
   */
  protected translate: TranslateService

  /**
   * Router service
   */
  protected router: Router

  protected route: ActivatedRoute

  /**
   * Dialog service used to display confirmation popup
   */
  protected dialogService: MatDialog

  /**
   * Material service used to create toasts
   */
  private snackBar: MatSnackBar

  /**
   * Dispose bag. It's purpose is to contain all RxJs Subscription object
   * generated when subscribing to an Observable. When destroying the component
   * all Subscriptions are unsubscribed to prevent memory leak.
   */
  protected disposeBag: Subscription[]

  constructor() {
    // Fetch the main injector.
    try {
      const injector = AppInjector.getInjector()

      // injector is undefined at this point
      this.translate = injector.get(TranslateService)
      this.router = injector.get(Router)
      this.route = injector.get(ActivatedRoute)
      this.dialogService = injector.get(MatDialog)
      this.snackBar = injector.get(MatSnackBar)
    } catch (e) {
      console.error('Failed initializing dependencies', e)
    }
    this.disposeBag = []
  }

  /**
   * Handle the message provided and displays it to the user.
   * If no DisplayType if provided a popup is showed by default.
   */
  protected handleMessage(message: Message, sType?: DisplayType) {
    switch (sType) {
      case DisplayType.toast:
        this.showToast(message)
        break
      default:
        this.showPopup(message)
        break
    }
  }

  /**
   * Shows a modal popup describing message or error.
   * Provides one or two actions to the user
   */
  private showPopup(message: Message) {
    if (message instanceof AppError) {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      this.dialogService
        .open(ErrorDialogComponent, {
          data: {
            error: message,
            header: this.translate.instant('DIALOG.ERROR')
          }
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            if (message.retryAction) {
              message.retryAction()
            } else if (message.okAction) {
              message.okAction()
            }
          } else if (message.okAction) {
            message.okAction()
          }
        })
    } else {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      this.dialogService
        .open(MessageDialogComponent, {
          data: {
            message: message,
            header: this.translate.instant('DIALOG.MESSAGE')
          }
        })
        .afterClosed()
        .subscribe((result) => {
          if (result && message.okAction) {
            message.okAction()
          }
        })
    }
  }

  /**
   * Shows a non-blocking toast at the bottom of the screen.
   * May provide retry action to the user.
   */
  private showToast(message: Message) {
    if (message instanceof AppError && message.shouldRetryOnFailure) {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      this.snackBar
        .open(
          message.message,
          message.okLabel || this.translate.instant('DIALOG.RETRY'),
          this.TOAST_CONFIG
        )
        .onAction()
        .subscribe(() => {
          message.retryAction()
        })
    } else {
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      this.snackBar.open(message.message, null, this.TOAST_CONFIG)
    }
  }

  /// LIFECYCLE HOOKS

  /**
   * Lifecycle Hook responsible for unsubscribing from every component
   * filling the disposeBag.
   */
  ngOnDestroy() {
    this.disposeBag.forEach((subscription: Subscription) => {
      subscription.unsubscribe()
    })
  }
}
